import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import "./../styles/theme.css";
import AdvancedSearch from './AdvancedSearch';
import recordService from '../services/RecordService';
import { useAuth } from '../context/AuthContext';
import ImageModal from './modals/ImageModal';
import useConfirmation from './../hooks/useConfirmation';
import useConfirmationAction from './../hooks/useConfirmationAction';
import './../styles/ImageGrid.css';
import './../styles/ImageGridActionMenu.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faCircleMinus, faTriangleExclamation, faUndo, faChevronUp, faEye, faStickyNote, faCopy } from '@fortawesome/free-solid-svg-icons';
import { BiArrowToTop, BiArrowFromTop  } from "react-icons/bi";
import { COLORS } from './../constants/colours';
import './../styles/ImageGridScroll.css';
import { useSite } from './../context/SiteContext';
import { useFilterContext } from '../context/FilterContext';
import { useUIContext } from './../context/UIContext';
import './../styles/ImageBlur.css';
import formatDateTime from './../utils/formatDateTime';
import './../styles/messageContainer.css';
import { UserType } from './../constants/userType';
import { useSecureMediaUrl } from './../hooks/useSecureMediaUrl';

const ImageGrid = () => {
  const { token, loggedInUserName } = useAuth();
  const { selectedSiteId } = useSite();
  const { showConfirmation, ConfirmationBox } = useConfirmation();
  const { showConfirmationAction, ConfirmationBoxAction } = useConfirmationAction();
  const { filters, setFilters, pagination, setPagination } = useFilterContext();
  const [images, setImages] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const { isBlurred } = useUIContext();
  const [previousImages, setPreviousImages] = useState([]); // as a fallback during loading
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const imageGridRef = useRef(null);
  const imageGridShellRef = useRef(null);
  const { isCollapsed } = useFilterContext();
  const [showScrollTop, setShowScrollTop] = useState(true);
  const [showScrollFromTop, setShowScrollFromTop] = useState(true);
  const { userType } = useAuth();
  const isPoliceUser = userType === UserType.POLICE;
  const isSuperUser = (userType === UserType.TEAMLEADER || userType === UserType.SUPER);
  const SORT = { DATE: 0, VISOR: 1, DESCRIPTION: 4, ANALYST: 5 };
  const { toSecureMediaUrl } = useSecureMediaUrl();
  const [gridShellHeight, setGridShellHeight] = useState(null);

  useLayoutEffect(() => {
    const updateGridShellHeight = () => {
      if (!imageGridShellRef.current) return;

      const { top } = imageGridShellRef.current.getBoundingClientRect();
      const nextHeight = Math.max(320, Math.floor(window.innerHeight - top));
      setGridShellHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    };

    updateGridShellHeight();

    const shellElement = imageGridShellRef.current;
    const filterPanelElement = shellElement?.previousElementSibling;
    const parentElement = shellElement?.parentElement;
    const animationFrame = window.requestAnimationFrame(updateGridShellHeight);
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            window.requestAnimationFrame(updateGridShellHeight);
          })
        : null;

    if (resizeObserver && filterPanelElement) {
      resizeObserver.observe(filterPanelElement);
    }

    if (resizeObserver && parentElement) {
      resizeObserver.observe(parentElement);
    }

    filterPanelElement?.addEventListener('transitionend', updateGridShellHeight);
    window.addEventListener('resize', updateGridShellHeight);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      filterPanelElement?.removeEventListener('transitionend', updateGridShellHeight);
      window.removeEventListener('resize', updateGridShellHeight);
    };
  }, [isCollapsed]);

  const adjustCounts = (removedCount) => {
    setTotalRecords(prevTotal => {
      const newTotal = Math.max(0, prevTotal - removedCount);
      // recalc pages based on pageSize
      const newPages = Math.max(
        1,
        Math.ceil(newTotal / pagination.pageSize)
      );
      setTotalPages(newPages);
      // if currentPage is now out of range, bring it back
      if (pagination.currentPage > newPages) {
        setPagination(prev => ({
          ...prev,
          currentPage: newPages
        }));
      }
      return newTotal;
    });
  };

  const fetchImages = async () => {
    setPreviousImages(images);
  
    const loadingDelay = setTimeout(() => {
      setIsLoading(true);
    }, 300);
  
    try {
      const response = await recordService.getRecords(selectedSiteId, {
        ...filters,
        pageSize: pagination.pageSize,
        pageIndex: pagination.currentPage,
      });
  
      if (Array.isArray(response.data)) {
        const loadedImages = response.data.map((record) => ({
          image_id: record.image_id,
          url: toSecureMediaUrl(record.image_name),
          description: record.image_description || '',
          image_description: record.image_description || '',
          dedups: Number(record.dedups) || 0,
          report_category: record.report_category,
          colour: record.colour || '#000',
          visor_ref: record.visor_ref,
          positive: record.positive,
          status: record.status,
          action: record.action,
          report_date: record.report_date,
          lastUpdated: record.lastUpdated,
          reviewedby: record.reviewedby,
          quarantine: record.quarantine,
          groupName: record.groupName,
          model: record.model,
          imageAppObservation: record.imageAppObservation,
          isViewed: record.isViewed || false,
        }));
  
        setImages(loadedImages);
        setTotalPages(response.totalPages);
        setTotalRecords(response.totalCount || loadedImages.length);
        setError(null);
      } else {
        setImages([]);
        setTotalPages(1);
        setTotalRecords(0);
        setError('There are no images queued for review at this time. Please ensure the filters above are set correctly (e.g. Status is set to “Not reviewed” by default).');
      }
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Unable to retrieve records. Your session may have timed out. Please try logging in again.');
    } finally {
      clearTimeout(loadingDelay);
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchImages();
  }, [selectedSiteId, filters, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    const container = imageGridRef.current;
    if (!container) return;

    const handleScroll = () => {
      setShowScrollTop(container.scrollTop);
      setShowScrollFromTop(container.scrollTop);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const recalculatePageInfo = (remainingRecords) => {
    const newTotalPages = Math.ceil(remainingRecords / pagination.pageSize);
    setTotalRecords(remainingRecords);
    setTotalPages(newTotalPages);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
    scrollGridToTop();
  };

  const handlePageSizeChange = (e) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: Number(e.target.value),
      currentPage: 1,
    }));
  };

  const handleImageClick = (image) => {
    const index = images.findIndex((img) => img.image_id === image.image_id);
    setSelectedImage(image);
    setCurrentIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedImage(null);
    setIsModalOpen(false);
  };

  const toggleSelectAll = (e) => {
    setSelectedImages(e.target.checked ? images.map((img) => img.image_id) : []);
  };

  const getUnreviewedImageIds = () =>
    images.filter((img) => !img.reviewedby).map((img) => img.image_id);
  
  const toggleSelectAllUnreviewed = (e) => {
    const unreviewedImageIds = getUnreviewedImageIds();
    setSelectedImages(e.target.checked ? unreviewedImageIds : selectedImages.filter(id => !unreviewedImageIds.includes(id)));
  };
  
  const areAllUnreviewedSelected = () => {
    const unreviewedImageIds = getUnreviewedImageIds();
    return (
      unreviewedImageIds.length > 0 &&
      unreviewedImageIds.every((id) => selectedImages.includes(id))
    );
  };

  const toggleImageSelection = (imageId, index, isShift) => {
    const idsOnScreen = visibleImages.map(img => img.image_id);
  
    if (isShift && lastSelectedIndex !== null) {
      // ► range-select
      const [start, end] = [
        Math.min(lastSelectedIndex, index),
        Math.max(lastSelectedIndex, index),
      ];
  
      const rangeIds = idsOnScreen.slice(start, end + 1);
      setSelectedImages(prev => Array.from(new Set([...prev, ...rangeIds])));
    } else {
      // ► normal toggle
      setSelectedImages(prev =>
        prev.includes(imageId)
          ? prev.filter(id => id !== imageId)
          : [...prev, imageId]
      );
      setLastSelectedIndex(index);        // remember pivot for next Shift-click
    }
  };
  
  
  const updateImageInGrid = (updatedImage) => {
    setImages((prev) =>
      prev.map((img) => {
        if (img.image_id !== updatedImage.image_id) return img;

        const nextDescription =
          updatedImage.image_description ??
          updatedImage.description ??
          img.image_description ??
          '';

        const nextCategory =
          updatedImage.report_category ??
          updatedImage.category ??
          img.report_category ??
          '';

        return {
          ...img,
          ...updatedImage,
          description: nextDescription,
          image_description: nextDescription,
          report_category: nextCategory,
          category: nextCategory,
        };
      })
    );
  };

  const buildRecordUpdate = (imageId, updates = {}) => {
    const current = images.find((img) => img.image_id === imageId) || {};
    const description =
      updates.image_description ??
      updates.description ??
      current.image_description ??
      current.description ??
      '';
    const category =
      updates.report_category ??
      updates.category ??
      current.report_category ??
      current.category ??
      '';

    return {
      image_id: imageId,
      imageId,
      recordId: imageId,
      ...updates,
      image_description: description,
      imageDescription: description,
      description,
      report_category: category,
      reportCategory: category,
      category,
    };
  };

  const handleMarkPositive = () => {
    showConfirmation(
      'Do you want to mark the selected images as Important or Not Important?',
      [
        {
          label: 'Mark as Important',
          action: async () => {
            try {
              setIsLoading(true);
              const updatedRecords = selectedImages.map((imageId) => buildRecordUpdate(imageId, {
                positive: 1,
                status: 0,
                colour: COLORS.POSITIVE_IMPORTANT,
                reviewedby: loggedInUserName || 'You'
              }));
              await recordService.updateRecords(updatedRecords);
              updatedRecords.forEach(updateImageInGrid);
              adjustCounts(updatedRecords.length);
              setSelectedImages([]);
              setIsLoading(false);
            } catch (error) {
              console.error('Failed to update records:', error);
              setIsLoading(false);
            }
          },
        },
        {
          label: 'Mark as Not Important',
          action: async () => {
            try {
              setIsLoading(true);
              const updatedRecords = selectedImages.map((imageId) => buildRecordUpdate(imageId, {
                positive: 1,
                status: 1,
                colour: COLORS.POSITIVE_NOT_IMPORTANT,
                reviewedby: loggedInUserName || 'You'
              }));
              await recordService.updateRecords(updatedRecords);
              updatedRecords.forEach(updateImageInGrid);
              adjustCounts(updatedRecords.length);
              setSelectedImages([]);
              setIsLoading(false);
            } catch (error) {
              console.error('Failed to update records:', error);
              setIsLoading(false);
            }
          },
        },
      ],
      () => console.log('Cancelled')
    );
  };

  const handleMarkFalsePositive = () => {
    showConfirmation(
      'Do you want to mark the selected images as Important or Not Important?',
      [
        {
          label: 'Mark as Important',
          action: async () => {
            try {
              setIsLoading(true); 
              const updatedRecords = selectedImages.map((imageId) => buildRecordUpdate(imageId, {
                positive: 2,
                status: 0,
                colour: COLORS.FALSE_POSITIVE_IMPORTANT,
                reviewedby: loggedInUserName || 'You'
              }));
              await recordService.updateRecords(updatedRecords);
              updatedRecords.forEach(updateImageInGrid);
              adjustCounts(updatedRecords.length);
              setSelectedImages([]);
              setIsLoading(false);
            } catch (error) {
              console.error('Failed to update records:', error);
              setIsLoading(false);
            }
          },
        },
        {
          label: 'Mark as Not Important',
          action: async () => {
            try {
              setIsLoading(true);
              const updatedRecords = selectedImages.map((imageId) => buildRecordUpdate(imageId, {
                positive: 2,
                status: 1,
                colour: COLORS.FALSE_POSITIVE,
                reviewedby: loggedInUserName || 'You'
              }));
              await recordService.updateRecords(updatedRecords);
              updatedRecords.forEach(updateImageInGrid);
              adjustCounts(updatedRecords.length);
              setSelectedImages([]);
              setIsLoading(false);
            } catch (error) {
              console.error('Failed to update records:', error);
              setIsLoading(false);
            }
          },
        },
      ],
      () => console.log('Cancelled')
    );
  };

  const handleMarkQuarantine = () => {
    showConfirmation(
      'Do you want to Quarantine the selected images?',
      [
        {
          label: 'Yes',
          action: async () => {
            try {
              setIsLoading(true);
              const updatedRecords = selectedImages.map((imageId) => buildRecordUpdate(imageId, {
                quarantine: 1,
                colour: COLORS.QUARANTINE,
                reviewedby: loggedInUserName || 'You'
              }));
              await recordService.updateRecords(updatedRecords);
              updatedRecords.forEach(updateImageInGrid);
              adjustCounts(updatedRecords.length);
              setSelectedImages([]);
              setIsLoading(false);
            } catch (error) {
              console.error('Failed to update records:', error);
              setIsLoading(false);
            }
          },
        },
      ],
      () => console.log('Cancelled')
    );
  };

  const handleMarkUnreviewed = () => {
    showConfirmation(
      'Do you want to mark the selected images as Unreviewed?',
      [
        {
          label: 'Mark as Unreviewed',
          action: async () => {
            try {
              setIsLoading(true);
              const updatedRecords = selectedImages.map((imageId) => buildRecordUpdate(imageId, {
                positive: 0,
                status: 2,
                colour: COLORS.NOT_REVIEWED,
                reviewedby: '',
                quarantine: 0,
              }));
              await recordService.updateRecords(updatedRecords);
              updatedRecords.forEach(updateImageInGrid);
              adjustCounts(updatedRecords.length);
              setSelectedImages([]);
              setIsLoading(false);
            } catch (error) {
              console.error('Failed to update records:', error);
              setIsLoading(false);
            }
          },
        },
      ],
      () => console.log('Cancelled')
    );
  };

  const handleMarkAction = () => {
  showConfirmationAction(
    'Do you want to mark the selected images as "Action required"?',
    [
      {
        label: 'Mark as Required',
        action: async () => {
          try {
            setIsLoading(true);

            const payload = selectedImages.map((id) => ({
              recordId: id,     
              isAction: true,   
            }));

            const updatedRecords = selectedImages.map((id) => ({
              image_id: id,
              action: true,
              colour: COLORS.ACTION_REQUIRED,
            }));

            await recordService.setActionAll(payload);
            updatedRecords.forEach(updateImageInGrid);
            adjustCounts(updatedRecords.length);
            setSelectedImages([]);
          } catch (error) {
            console.error('Failed to update records:', error);
          } finally {
            setIsLoading(false);
          }
        },
      },
      {
        label: 'Mark as Not Required',
        action: async () => {
          try {
            setIsLoading(true);

            const payload = selectedImages.map((id) => ({
              recordId: id,
              isAction: false,
            }));

            const updatedRecords = selectedImages.map((id) => ({
              image_id: id,
              action: false,
              colour: COLORS.ACTION_NOT_REQUIRED,
            }));

            await recordService.setActionAll(payload);
            updatedRecords.forEach(updateImageInGrid);
            adjustCounts(updatedRecords.length);
            setSelectedImages([]);
          } catch (error) {
            console.error('Failed to update records:', error);
          } finally {
            setIsLoading(false);
          }
        },
      },
    ],
    () => console.log('Cancelled')
  );
};

  const visibleImages = isLoading ? previousImages : images;

  const startRecord =
  totalRecords === 0
    ? 0
    : (pagination.currentPage - 1) * pagination.pageSize + 1;
const endRecord = Math.min(
  pagination.currentPage * pagination.pageSize,
  totalRecords
);

const scrollGridToTop = () => {
    if (imageGridRef.current) {
      imageGridRef.current.scrollTo({
        top: 0,
        behavior: 'instant',
      });
    }
  };

  const scrollGridToBottom = () => {
    if (imageGridRef.current) {
      imageGridRef.current.scrollTo({
        top: imageGridRef.current.scrollHeight,
        behavior: 'instant',
      });
    }
  };

const [sortValue, setSortValue] = useState(
  typeof filters.SortColumn === 'number' ? filters.SortColumn : SORT.DATE
);

// keep UI in sync if filters.SortColumn changes elsewhere
useEffect(() => {
  if (typeof filters.SortColumn === 'number' && filters.SortColumn !== sortValue) {
    setSortValue(filters.SortColumn);
  }
}, [filters.SortColumn]); // eslint-disable-line react-hooks/exhaustive-deps

const handleSortChange = (e) => {
  const next = Number(e.target.value);
  setSortValue(next);
  setFilters(prev => ({ ...prev, SortColumn: next }));
  setPagination(prev => ({ ...prev, currentPage: 1 }));
  setShowScrollTop(false);
  setShowScrollFromTop(false);
};

  const gridStyle = {
    '--image-grid-shell-height': gridShellHeight ? `${gridShellHeight}px` : undefined,
  };

  return (
     <div className="app-page">
      <AdvancedSearch onRefreshGrid={scrollGridToTop}/>
      <div
        className={ isCollapsed ? 'image-custom-container-collapsed' : 'image-custom-container' }
        ref={imageGridShellRef}
        style={gridStyle}
      >
        <div className="action-bar">
          {!isPoliceUser && (<> 
            <div className="action-bar-select-all">
              <span>Unreviewed</span>
              <label className="imagegrid-checkbox-all">
                <input
                  type="checkbox"
                onChange={toggleSelectAllUnreviewed}
                checked={areAllUnreviewedSelected()}
              />
              <span className="checkmark"></span>
            </label>
            </div>
          </>)}
          
            <div className="action-bar-select-all">
              <span>All</span>
              <label className="imagegrid-checkbox-all">
                <input
                  type="checkbox"
                  onChange={toggleSelectAll}
                  checked={selectedImages.length === images.length && images.length > 0}
                />
                <span className="checkmark"></span>
              </label>
            </div>
            {isPoliceUser && (<>
            <button
              className="icon-button-action"
              onClick={handleMarkAction}
              title="Set Action"
            >Set Action</button>  
            </>)}
          {!isPoliceUser && (<>        
            <button
              className="icon-button-red"
              onClick={handleMarkPositive}
              title="Mark as Positive"
            >
              <FontAwesomeIcon icon={faPlusCircle} />
            </button>
            <button
              className="icon-button-green"
              onClick={handleMarkFalsePositive}
              title="Mark as False Positive"
            >
              <FontAwesomeIcon icon={faCircleMinus} />
            </button>
            <button
              className="icon-button-lilac"
              onClick={handleMarkQuarantine}
              title="Quarantine"
            >
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </button>
            <button
              className="icon-button-undo"
              onClick={handleMarkUnreviewed}
            >
              <FontAwesomeIcon icon={faUndo}/>
              <span className="toggle-text">Undo Review</span>
            </button>
            </>
          )}

          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange(1)}
              disabled={pagination.currentPage <= 1}
            >
              First
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage <= 1}
            >
              Previous
            </button>
            <input
              type="number"
              value={pagination.currentPage}
              onChange={(e) => handlePageChange(Number(e.target.value))}
              min={1}
              max={totalPages}
            />
            <b>/</b>
            <span><b>{totalPages}</b></span>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage >= totalPages}
            >
              Next
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={pagination.currentPage >= totalPages}
            >
              Last
            </button>
            <select value={pagination.pageSize} onChange={handlePageSizeChange}>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>

            
<select
  id="sortSelect"
  className="sort-select"
  value={sortValue}
  onChange={handleSortChange}
  title="Choose sort order"
>
  <option value={SORT.DATE}>By Date</option>
  <option value={SORT.VISOR}>By Visor</option>
  <option value={SORT.DESCRIPTION}>By Description</option>
  {isSuperUser && <option value={SORT.ANALYST}>By Analyst</option>}
</select>

          </div>
        </div>

        <div className="image-grid-scroll-container" ref={imageGridRef}>
          {isLoading && (
            <div className="grid-loading-overlay">
              <div className="loading-spinner" />
            </div>
          )}
          <div className={`custom-grid-container ${isBlurred ? 'blurred' : ''}`}>            
            {!isLoading && (error || visibleImages.length === 0) ? (
              <div className="message-container">
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  size="3x"
                  className="message-icon"
                />
                <p className="message-text">
                  {error || 'There are no images queued for review at this time. Please ensure the filters above are set correctly (e.g. Status is set to “Not reviewed” by default).'}
                </p>
              </div>
            ) : (
              /** Otherwise render your usual grid of images */
              visibleImages.map((image, index) => {
  const fileExt = image.url.split('.').pop().toLowerCase();
  const isVideo = ['mp4', 'webm', 'ogg'].includes(fileExt);
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExt);

  return (
    <div className="custom-grid-item" key={image.image_id}>
      <div className="card image-card">
        <div className="image-wrapper">
          {isVideo ? (
            <video
              src={image.url}
              className="card-img-top"
              style={{ border: `8px solid ${image.colour || 'WHITE'}` }}
              controls
              onClick={() => handleImageClick(image)}
            />
          ) : isImage ? (
            <img
              src={image.url}
              alt={image.description}
              className="card-img-top"
              style={{ border: `8px solid ${image.colour === 'WHITE' ? 'transparent' : image.colour}` }}
              onClick={() => handleImageClick(image)}
              loading="lazy"
            />
          ) : (
  <span>Unsupported file format</span>
)}

{image.image_description && (
    <div className="description-icon-overlay" title="Has Description">
      <FontAwesomeIcon icon={faStickyNote} />
    </div>
  )}
          {!isPoliceUser && image.dedups > 0 && (
            <div
              className="image-dedups-overlay"
              title={`${image.dedups} deduplicated matches`}
            >
              <FontAwesomeIcon icon={faCopy} className="image-dedups-icon" />
              <span className="image-dedups-count">{image.dedups}</span>
            </div>
          )}
          {image.isViewed && (
            <div
              className={`viewed-icon-overlay ${!isPoliceUser && image.dedups > 0 ? 'has-dedups' : ''}`}
              title="Viewed"
            >
              <FontAwesomeIcon icon={faEye} />
            </div>
          )}
        </div>

        <div className="card-body">
          <p
            className={
              image.isViewed
                ? 'imagegrid-card-text viewed'
                : 'imagegrid-card-text'
            }
          >
            <input
              className="imagegrid-checkbox"
              id={`cb-${image.image_id}`}
              type="checkbox"
              checked={selectedImages.includes(image.image_id)}
              onChange={(e) =>
                toggleImageSelection(
                  image.image_id,
                  index,
                  e.nativeEvent.shiftKey
                )
              }
            />
            <span className="checkmark"></span>
            <span className="imagegrid-card-datetime">
              {formatDateTime(new Date(image.report_date))}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
})

            
            )}
          </div>
        </div>
        <div className={`table-footer ${totalRecords > 0 ? 'has-entries' : 'is-empty'}`}>
        {totalRecords > 0 && (
          <span className="table-footer-pill">
           {`Showing ${startRecord} to ${endRecord} of ${totalRecords} entries`}
          </span>
        )}
     </div>

        {visibleImages.length > 0 && (
          <>
            <button
              className="scroll-to-from-top-btn"
              onClick={scrollGridToBottom}
              title="Scroll to bottom"
            >
              <BiArrowFromTop />
            </button>
            <button
              className="scroll-to-top-btn"
              onClick={scrollGridToTop}
              title="Scroll to top"
            >
              <BiArrowToTop />
            </button>
          </>
        )}
      </div>

      {isModalOpen && selectedImage && (
        <ImageModal
          image={images[currentIndex]}
          onClose={closeModal}
          token={token}
          images={images}
          isOpen={isModalOpen}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          updateImageInGrid={updateImageInGrid}
          contentLabel="Image Details"
          ariaHideApp={false}
renderImage={(image) => {
  const fileExt = image.url.split('.').pop().toLowerCase();
  const isVideo = ['mp4', 'webm', 'ogg'].includes(fileExt);

  return (
    <div className="modal-image-container">
      {isVideo ? (
        <video src={image.url} controls className="modal-video" />
      ) : (
        <img
          src={image.url}
          alt={image.description}
          className="modal-image"
        />
      )}
    </div>
  );
}}
        />
      )}

      <ConfirmationBox />
      <ConfirmationBoxAction />
    </div>
  );
};

export default ImageGrid;
